import { Button, Chip, Stack, Tooltip, Typography } from '@rossum/ui/material';
import { useState } from 'react';
import * as R from 'remeda';
import {
  calendlyLinks,
  calendlyLinkVariants,
} from '../../../features/surveys/DiscoveryCallSurvey/calendlyLinks';
import {
  discoveryCallsList,
  FeatureFlagSlug,
} from '../../../features/surveys/DiscoveryCallSurvey/discoveryCallsList';
import {
  discoveryCallsPrefix,
  useDiscoveryCallsFlag,
} from '../../../features/surveys/DiscoveryCallSurvey/useDiscoveryCallsFlag';
import CopyToClipboardButton from '../../../ui/copy-to-clipboard/CopyToClipboardButton';
import { ItemTile } from '../../../ui/item-tile/ItemTile';
import { unleashClient } from '../../../unleash/Unleash';

type DiscoveryCallTileProps = {
  triggerUrl: string;
  name: string;
  featureFlagSlug: FeatureFlagSlug;
};

const DiscoveryCallTile = ({
  triggerUrl,
  name: flagName,
  featureFlagSlug,
}: DiscoveryCallTileProps) => {
  const discoveryCallFlag = useDiscoveryCallsFlag(featureFlagSlug);

  const { name, enabled, calendlyLink } = discoveryCallFlag ?? {
    enabled: false,
    calendlyLink: null,
    name: `${discoveryCallsPrefix}-${featureFlagSlug}__(${calendlyLinkVariants.join('|')})`,
  };

  const calendlyLinkKey =
    R.entries(calendlyLinks).find(
      ([_, value]) => value === calendlyLink
    )?.[0] ?? '--';

  const calendlyLinkToDisplay =
    calendlyLinkKey.length > 3
      ? R.capitalize(calendlyLinkKey)
      : R.toUpperCase(calendlyLinkKey);

  return (
    <ItemTile
      sx={{ '> div': { height: 'unset', py: 1 } }}
      quickActions={
        <Stack pr={3}>
          <Chip
            label={
              calendlyLink ? 'Enabled' : enabled ? 'Missing link' : 'Disabled'
            }
            color={calendlyLink ? 'success' : enabled ? 'warning' : 'default'}
          />
        </Stack>
      }
    >
      <Stack
        px={3}
        alignSelf="center"
        sx={{
          width: '100%',
          color: t =>
            t.palette.text[discoveryCallFlag ? 'primary' : 'disabled'],
        }}
      >
        <Typography
          variant="h6"
          sx={{ minWidth: 0 }}
          overflow="hidden"
          whiteSpace="nowrap"
          textOverflow="ellipsis"
        >
          {flagName}
        </Typography>
        <Stack spacing={1} direction="row" alignItems="center">
          <Typography variant="caption">{name}</Typography>
          <CopyToClipboardButton
            buttonProps={{ size: 'small', color: 'inherit' }}
            content={name}
          />
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            color: t =>
              t.palette.text[discoveryCallFlag ? 'secondary' : 'disabled'],
          }}
          divider={
            <Typography variant="caption" component="span">
              •
            </Typography>
          }
        >
          <Typography
            sx={{ minWidth: 0 }}
            variant="caption"
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
          >
            {`Trigger URL: ${triggerUrl}`}
          </Typography>
          <Typography
            sx={{ minWidth: 0 }}
            variant="caption"
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
          >
            <span>{`Calendly link: `}</span>{' '}
            <Tooltip title={calendlyLink}>
              <span>{calendlyLinkToDisplay}</span>
            </Tooltip>
          </Typography>
        </Stack>
      </Stack>
    </ItemTile>
  );
};

export const DiscoveryCallsOverview = () => {
  const [refreshTimestamp, setRefreshTimestamp] = useState(0);
  return (
    <Stack key={refreshTimestamp} spacing={2} sx={{ width: '100%' }}>
      <Button
        onClick={() =>
          unleashClient
            .updateContext({})
            .then(() => setRefreshTimestamp(Date.now()))
        }
        sx={{ alignSelf: 'flex-end', '&&': { mt: -6 }, mt: -5 }}
        variant="outlined"
      >
        Refresh flags
      </Button>

      {discoveryCallsList.map(dc => {
        return (
          <DiscoveryCallTile
            key={dc.featureFlagSlug}
            triggerUrl={dc.triggerUrl}
            name={dc.name}
            featureFlagSlug={dc.featureFlagSlug}
          />
        );
      })}
    </Stack>
  );
};
